import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Users, Home } from "lucide-react";
import DecorativeDivider from "@/components/DecorativeDivider";
import PhotoCarousel from "@/components/PhotoCarousel";

const familyPhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/2hqabbam_Family%202.JPG",
    alt: "Family at airport",
    caption: "Traveling together",
    aspect: "4/3",
  },
];

const familyMembers = [
  {
    relation: "Father",
    name: "Ramnath Dangui",
    detail: "Retired Head Clerk",
    birthPlace: "Mapusa, Goa",
  },
  {
    relation: "Mother",
    name: "Ujwala Dangui",
    detail: "Retired Teacher",
    birthPlace: "Sanquelim, Goa",
    maidenName: "Ujwala Shet Verekar",
  },
  {
    relation: "Siblings",
    name: "Shruti Dangui (Elder Sister)",
    detail: "",
    birthPlace: "",
    tag: "Married",
  },
];

export default function FamilyBackground() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 md:py-16 px-4 sm:px-6 md:px-12"
      data-testid="family-section"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p
            className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 font-semibold"
            style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
          >
            Family
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6 md:mb-8"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Family Background
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* Family Info */}
          <div className="flex flex-col">
            {familyMembers.map((member, i) => (
              <motion.div
                key={member.relation}
                initial={{ opacity: 0, y: 16 }}
                animate={visible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className={`${i > 0 ? "pt-4 sm:pt-5" : ""} pb-4 sm:pb-5`}
                style={{ borderBottom: "1px solid #E6E2DD" }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Users className="w-4 h-4" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
                  <p
                    className="text-xs tracking-[0.15em] uppercase font-semibold"
                    style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {member.relation}
                  </p>
                </div>
                <p
                  className="text-base sm:text-lg font-medium mb-1"
                  style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
                >
                  {member.name}
                </p>
                {member.tag && (
                  <p
                    className="text-xs sm:text-sm font-light mt-0.5"
                    style={{ color: "#C4B298", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {member.tag}
                  </p>
                )}
                {member.detail && (
                  <p className="text-xs sm:text-sm font-light" style={{ color: "#999999", fontFamily: "'Manrope', sans-serif" }}>
                    {member.detail}
                  </p>
                )}
                {member.maidenName && (
                  <p className="text-xs sm:text-sm font-light mt-0.5" style={{ color: "#999999", fontFamily: "'Manrope', sans-serif" }}>
                    Maiden name: {member.maidenName}
                  </p>
                )}
                {member.birthPlace && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Home className="w-3 h-3" style={{ color: "#C4B298" }} strokeWidth={1.5} />
                    <p className="text-xs sm:text-sm font-light" style={{ color: "#C4B298", fontFamily: "'Manrope', sans-serif" }}>
                      {member.birthPlace}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="pt-4 sm:pt-5"
            >
              <p
                className="text-sm sm:text-base leading-relaxed font-light"
                style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif", opacity: 0.85 }}
              >
                We are a respectable, upper-middle-class family with culturally grounded values. The upbringing has been supportive, value-driven, and centered around mutual respect and warmth.
              </p>
            </motion.div>
          </div>

          {/* Family Photos Carousel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={visible ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="max-w-sm sm:max-w-md mx-auto lg:max-w-none w-full"
          >
            <PhotoCarousel photos={familyPhotos} />
          </motion.div>
        </div>

        <div className="mt-8 sm:mt-10">
          <DecorativeDivider />
        </div>
      </div>
    </section>
  );
}
